import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CoverageMapResponse,
  CoveragePostcode,
  TechCoverageSummary,
  TechSummary,
  TechUnavailability,
} from "@breeze/shared";
import { TopNav } from "./TopNav.js";
import { Section } from "./BusinessGrowth.js";
import { CoverageMap, type CoverageMapMode } from "./CoverageMap.js";
import {
  createUnavailability,
  deleteUnavailability,
  evaluateIdle,
  fetchCoverageMap,
  fetchCoverageSummary,
} from "../api.js";
import {
  getPermission,
  isEnabled as isNotifyEnabled,
  isSupported as isNotifySupported,
  requestPermission as requestNotifyPermission,
  setEnabled as setNotifyEnabled,
} from "../lib/browserNotify.js";

// ===========================================================================
// Tech Coverage dashboard — Phase 1 (busy/idle, no map yet).
//
// Three sections:
//   - Idle alert banner (only renders when at least one active tech is idle)
//   - Per-tech grid: nickname, last job, days idle, upcoming, capacity
//   - "Run idle check now" button for manual sweeps (n8n cron also runs daily)
//
// Phase 2 will add the map; Phase 3 wires browser notifications. The page
// already listens for `technician.idle_changed` SSE events (in useRealtime)
// so the banner refreshes the moment the cron flips someone's state.
// ===========================================================================

type Props = { userName: string };

const THRESHOLD_DAYS = 14;

// Stage-palette colours — one per active tech, in load order. Mirrors the
// chart palette used in Business Growth so each tech has a stable visual id.
const TECH_PALETTE = [
  "#36C5F0", "#ECB22E", "#2EB67D", "#F07B3F",
  "#7F6DC5", "#E01E5A", "#A678A8", "#611F69",
];

function formatGBP(amount: number, opts: { decimals?: 0 | 2 } = {}): string {
  const decimals = opts.decimals ?? 2;
  const abs = Math.abs(amount).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return amount < 0 ? `−£${abs}` : `£${abs}`;
}

function formatDate(iso: string): string {
  // YYYY-MM-DD → "8 May 2026"
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${Number(m[3])} ${months[Number(m[2]) - 1]} ${m[1]}`;
}

export function TechCoverage({ userName }: Props): JSX.Element {
  const qc = useQueryClient();
  const summary = useQuery<TechCoverageSummary>({
    queryKey: ["coverage", "summary", THRESHOLD_DAYS],
    queryFn: () => fetchCoverageSummary(THRESHOLD_DAYS),
  });

  const [evaluating, setEvaluating] = useState(false);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [evalNote, setEvalNote] = useState<string | null>(null);

  async function handleRunCheck(): Promise<void> {
    setEvaluating(true);
    setEvalError(null);
    setEvalNote(null);
    try {
      const r = await evaluateIdle(THRESHOLD_DAYS);
      if (r.changed.length === 0) {
        setEvalNote(`Checked ${r.evaluated} techs — no state changes`);
      } else {
        const flipped = r.changed.map((c) => `${c.nickname} → ${c.isIdle ? "idle" : "back"}`).join(", ");
        setEvalNote(`Checked ${r.evaluated} techs — flipped: ${flipped}`);
      }
      await qc.invalidateQueries({ queryKey: ["coverage"] });
    } catch (err) {
      setEvalError((err as Error).message);
    } finally {
      setEvaluating(false);
    }
  }

  const idleTechs = (summary.data?.techs ?? []).filter((t) => t.idleAlert.triggered);

  return (
    <div className="flex h-full flex-col bg-ink-800">
      <TopNav userName={userName} />
      <main className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
                Tech Coverage
              </div>
              <h1 className="mt-1 text-2xl font-semibold text-ink-50">
                Active technicians
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <NotifyToggle />
              <button
                onClick={handleRunCheck}
                disabled={evaluating}
                className="rounded-md bg-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
              >
                {evaluating ? "Checking…" : "Run idle check"}
              </button>
            </div>
          </div>

          {evalError && (
            <div className="rounded-md border border-stage-lost/40 bg-stage-lost/10 p-2 text-[13px] text-stage-lost">
              {evalError}
            </div>
          )}
          {evalNote && !evalError && (
            <div className="rounded-md border border-stage-won/40 bg-stage-won/10 p-2 text-[13px] text-stage-won">
              {evalNote}
            </div>
          )}

          {idleTechs.length > 0 && <IdleBanner techs={idleTechs} />}

          {summary.isLoading ? (
            <div className="text-[15px] text-ink-100">Loading…</div>
          ) : !summary.data || summary.data.techs.length === 0 ? (
            <div className="text-[15px] text-ink-100">No active technicians.</div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {summary.data.techs.map((t, i) => (
                <TechCard
                  key={t.technicianId}
                  tech={t}
                  color={TECH_PALETTE[i % TECH_PALETTE.length] ?? "#A678A8"}
                />
              ))}
            </div>
          )}

          <MapSection />

          <Section
            title="How this works"
            subtitle="Idle detection criteria"
          >
            <div className="space-y-2 text-[15px] text-ink-50">
              <p>
                A tech is flagged <span className="font-semibold text-stage-lost">idle</span> when:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>No completed visit in the last {THRESHOLD_DAYS} days, AND</li>
                <li>No booked visit in the next 7 days.</li>
              </ul>
              <p className="mt-3 text-ink-100">
                State is re-evaluated automatically every morning at 08:00 (Europe/London) via n8n
                cron, plus on demand via the <span className="font-mono text-[13px]">Run idle check</span> button above.
                Whenever a tech flips state (idle → back, or back → idle) the dashboard refreshes
                live across all connected boards.
              </p>
            </div>
          </Section>
        </div>
      </main>
    </div>
  );
}

function NotifyToggle(): JSX.Element {
  const [enabled, setEnabledState] = useState<boolean>(() => isNotifyEnabled());
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(() =>
    getPermission()
  );

  // Re-read state on mount in case another tab toggled it.
  useEffect(() => {
    setEnabledState(isNotifyEnabled());
    setPermission(getPermission());
  }, []);

  if (!isNotifySupported() || permission === "unsupported") {
    return (
      <span className="text-[13px] text-ink-100">Browser notifications not supported</span>
    );
  }

  async function handleToggle(): Promise<void> {
    if (enabled) {
      setNotifyEnabled(false);
      setEnabledState(false);
      return;
    }
    const result = await requestNotifyPermission();
    setPermission(result);
    if (result === "granted") {
      setNotifyEnabled(true);
      setEnabledState(true);
    }
  }

  const labelOff = permission === "denied" ? "Notifications blocked" : "Notify when idle";
  return (
    <button
      onClick={() => void handleToggle()}
      disabled={permission === "denied"}
      className={`rounded-md border px-3 py-2 text-[13px] font-semibold transition-colors disabled:opacity-50 ${
        enabled
          ? "border-stage-won/40 bg-stage-won/10 text-stage-won"
          : "border-ink-500 bg-ink-700 text-ink-100 hover:bg-ink-600 hover:text-ink-50"
      }`}
      title={
        permission === "denied"
          ? "Permission was blocked — re-enable in browser site settings"
          : enabled
          ? "Click to disable browser notifications"
          : "Click to enable browser notifications"
      }
    >
      {enabled ? "✓ Notifications on" : labelOff}
    </button>
  );
}

function MapSection(): JSX.Element {
  const [mode, setMode] = useState<CoverageMapMode>("activity");
  const [selected, setSelected] = useState<string | null>(null);
  const q = useQuery<CoverageMapResponse>({
    queryKey: ["coverage", "map", 90],
    queryFn: () => fetchCoverageMap(90),
  });

  const selectedPc: CoveragePostcode | null = useMemo(() => {
    if (!selected || !q.data) return null;
    return q.data.postcodes.find((p) => p.outward === selected) ?? null;
  }, [selected, q.data]);

  return (
    <Section
      title="Coverage map"
      subtitle="Last 90 days · CARTO Dark tiles · circle size = jobs, colour = revenue (or tech)"
      right={
        <div className="flex items-center gap-1 rounded-md border border-ink-500 bg-ink-700 p-0.5">
          <ModeButton label="Activity" active={mode === "activity"} onClick={() => setMode("activity")} />
          <ModeButton label="Coverage" active={mode === "tech"} onClick={() => setMode("tech")} />
        </div>
      }
    >
      {q.isLoading ? (
        <div className="text-[15px] text-ink-100">Loading map data…</div>
      ) : !q.data ? (
        <div className="text-[15px] text-ink-100">No data.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <CoverageMap
              data={q.data}
              mode={mode}
              selectedOutward={selected}
              onSelect={setSelected}
            />
            <div className="mt-2 flex items-center gap-3 text-[13px] text-ink-100">
              <Legend mode={mode} techs={q.data.techs} />
              <span className="ml-auto">{q.data.postcodes.length} outward postcodes plotted</span>
            </div>
          </div>
          <PostcodeDetail data={q.data} pc={selectedPc} onClear={() => setSelected(null)} />
        </div>
      )}
    </Section>
  );
}

function ModeButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`rounded-md px-3 py-1 text-[13px] font-semibold transition-colors ${
        active ? "bg-aubergine text-ink-50" : "text-ink-100 hover:bg-ink-600 hover:text-ink-50"
      }`}
    >
      {label}
    </button>
  );
}

function Legend({
  mode,
  techs,
}: {
  mode: CoverageMapMode;
  techs: { technicianId: number; nickname: string }[];
}): JSX.Element {
  if (mode === "tech") {
    return (
      <div className="flex flex-wrap items-center gap-3">
        {techs.map((t, i) => (
          <span key={t.technicianId} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ background: TECH_PALETTE[i % TECH_PALETTE.length] ?? "#A678A8" }}
            />
            <span>{t.nickname}</span>
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 text-ink-100">
          <span className="inline-block h-3 w-3 rounded-full border border-ink-500" />
          <span>uncovered</span>
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-full" style={{ background: "#36C5F0" }} />
        <span>low</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-full" style={{ background: "#2EB67D" }} />
        <span>steady</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-full" style={{ background: "#ECB22E" }} />
        <span>busy</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-full" style={{ background: "#E01E5A" }} />
        <span>top</span>
      </span>
    </div>
  );
}

function PostcodeDetail({
  data,
  pc,
  onClear,
}: {
  data: CoverageMapResponse;
  pc: CoveragePostcode | null;
  onClear: () => void;
}): JSX.Element {
  if (!pc) {
    return (
      <div className="rounded-lg border border-ink-700 bg-ink-700/40 p-4 text-[13px] text-ink-100">
        Click an outward on the map for details.
      </div>
    );
  }
  const techsCovering = pc.techIds
    .map((id) => data.techs.find((t) => t.technicianId === id))
    .filter((t): t is { technicianId: number; nickname: string } => Boolean(t));
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-700/40 p-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xl font-semibold text-ink-50 font-mono">{pc.outward}</h3>
        <button
          onClick={onClear}
          className="text-[13px] text-ink-100 hover:text-ink-50"
        >
          ✕ clear
        </button>
      </div>
      {pc.region && (
        <div className="mt-1 text-[13px] text-ink-100">{pc.region}</div>
      )}
      <div className="mt-4 space-y-2 text-[15px] text-ink-50">
        <div className="flex items-baseline justify-between">
          <span className="text-ink-100">Jobs (90d)</span>
          <span className="font-semibold tabular-nums">{pc.jobCount}</span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-ink-100">Revenue (90d)</span>
          <span className="font-semibold tabular-nums">£{pc.revenue.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</span>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">Techs covering</div>
        {techsCovering.length === 0 ? (
          <div className="mt-1 rounded-md bg-stage-lost/20 px-2 py-1 text-[13px] text-stage-lost">
            Nobody covers this — opportunity to recruit
          </div>
        ) : (
          <ul className="mt-2 space-y-1 text-[15px] text-ink-50">
            {techsCovering.map((t) => (
              <li key={t.technicianId}>{t.nickname}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function IdleBanner({ techs }: { techs: TechSummary[] }): JSX.Element {
  return (
    <div className="rounded-xl border border-stage-lost/50 bg-stage-lost/10 p-5">
      <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-stage-lost">
        Idle alert
      </div>
      <h2 className="mt-1 text-xl font-semibold text-ink-50">
        {techs.length === 1
          ? `${techs[0]?.nickname} hasn't worked in a while`
          : `${techs.length} technicians look idle`}
      </h2>
      <ul className="mt-3 space-y-1 text-[15px] text-ink-50">
        {techs.map((t) => (
          <li key={t.technicianId} className="flex items-center justify-between">
            <span>
              <span className="font-semibold">{t.nickname}</span>
              <span className="ml-2 text-ink-100">
                {t.daysSinceLastJob === null
                  ? "no completed jobs on record"
                  : `${t.daysSinceLastJob} days since last job`}
              </span>
            </span>
            <span className="text-[13px] text-ink-100">
              {t.idleAlert.daysToNextBooking === null
                ? "nothing booked"
                : `next booking in ${t.idleAlert.daysToNextBooking} days`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TechCard({ tech, color }: { tech: TechSummary; color: string }): JSX.Element {
  const qc = useQueryClient();
  const idle = tech.idleAlert.triggered;
  const capacityPct = Math.min(100, Math.round((tech.capacityPctNext14d ?? 0) * 100));
  const last = tech.lastCompletedJob;

  // 5 working days/week × 4 weeks lookahead, scaled by avg jobs/day
  // and discounted by known unavailability days. Gives a "what we can
  // realistically dispatch over the next month" number that already
  // accounts for holidays.
  const next30Workdays = 22; // ~ Mon-Fri × 4.4 weeks
  const availableNext30 = Math.max(0, next30Workdays - tech.unavailableWorkingDaysNext30);
  const expectedJobsNext30 = Math.round(tech.avgJobsPerWorkingDay * availableNext30);

  const [showAdd, setShowAdd] = useState(false);

  return (
    <div className="rounded-xl border border-ink-700 bg-ink-700/60 p-5 shadow-card">
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 rounded-full"
            style={{ background: color }}
          />
          <h3 className="text-xl font-semibold text-ink-50">{tech.nickname}</h3>
        </div>
        {idle ? (
          <span className="rounded-md bg-stage-lost/20 px-2 py-0.5 text-[13px] font-semibold text-stage-lost">
            On holiday?
          </span>
        ) : tech.unavailableWorkingDaysNext30 >= 5 ? (
          <span className="rounded-md bg-stage-opportunity/20 px-2 py-0.5 text-[13px] font-semibold text-stage-opportunity">
            Time off scheduled
          </span>
        ) : (
          <span className="rounded-md bg-stage-won/20 px-2 py-0.5 text-[13px] font-semibold text-stage-won">
            Working
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <Stat
          label="Last job"
          value={last ? formatDate(last.date) : "—"}
          hint={last ? `${last.postcodeOutward ?? last.postcode ?? ""} · ${formatGBP(last.jobValue, { decimals: 0 })}` : "no completed jobs"}
        />
        <Stat
          label="Avg jobs/day"
          value={tech.avgJobsPerWorkingDay > 0 ? tech.avgJobsPerWorkingDay.toFixed(1) : "—"}
          hint="last 90d (working days only)"
        />
        <Stat
          label="Upcoming next 14d"
          value={String(tech.upcomingVisits)}
          hint={tech.upcomingValue > 0 ? formatGBP(tech.upcomingValue, { decimals: 0 }) : "nothing booked"}
        />
        <Stat
          label="Realistic next 30d"
          value={String(expectedJobsNext30)}
          hint={
            tech.unavailableWorkingDaysNext30 > 0
              ? `${availableNext30}/${next30Workdays} workdays available`
              : `${next30Workdays} workdays at full availability`
          }
          accent={tech.unavailableWorkingDaysNext30 >= 5 ? "text-stage-opportunity" : "text-ink-50"}
        />
      </div>

      {/* Time off — list + add button */}
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">
            Time off ({tech.upcomingUnavailability.length})
          </div>
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="rounded-md border border-ink-500 px-2 py-0.5 text-[11px] text-ink-50 hover:bg-ink-700"
          >
            {showAdd ? "− cancel" : "+ add"}
          </button>
        </div>
        {showAdd && (
          <UnavailabilityAddForm
            technicianId={tech.technicianId}
            onSaved={() => {
              setShowAdd(false);
              void qc.invalidateQueries({ queryKey: ["coverage"] });
            }}
          />
        )}
        {tech.upcomingUnavailability.length === 0 ? (
          <div className="mt-1 text-[12px] text-ink-100">None on file. Add upcoming holidays so capacity calcs stay honest.</div>
        ) : (
          <ul className="mt-2 space-y-1">
            {tech.upcomingUnavailability.map((u) => (
              <UnavailabilityRow key={u.id} u={u} />
            ))}
          </ul>
        )}
      </div>

      {tech.locationsCovered.length > 0 && (
        <div className="mt-4">
          <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">
            Coverage ({tech.locationsCovered.length} outwards)
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {tech.locationsCovered.slice(0, 30).map((p) => (
              <span
                key={p}
                className="rounded-md bg-ink-800 px-2 py-0.5 text-[13px] text-ink-50 font-mono"
              >
                {p}
              </span>
            ))}
            {tech.locationsCovered.length > 30 && (
              <span className="text-[13px] text-ink-100">+{tech.locationsCovered.length - 30} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UnavailabilityAddForm({
  technicianId,
  onSaved,
}: {
  technicianId: number;
  onSaved: () => void;
}): JSX.Element {
  const today = new Date().toISOString().slice(0, 10);
  const [startsOn, setStartsOn] = useState(today);
  const [endsOn, setEndsOn] = useState(today);
  const [reason, setReason] = useState("holiday");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await createUnavailability({
        technicianId,
        startsOn,
        endsOn,
        reason: reason || null,
      });
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-2 rounded-md border border-ink-700 bg-ink-800/40 p-2">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[12px] text-ink-100">
          From
          <input
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
            className="mt-0.5 w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
          />
        </label>
        <label className="text-[12px] text-ink-100">
          To (inclusive)
          <input
            type="date"
            value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)}
            className="mt-0.5 w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
          />
        </label>
      </div>
      <label className="mt-2 block text-[12px] text-ink-100">
        Reason
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-0.5 w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
        >
          <option value="holiday">Holiday</option>
          <option value="sick">Sick</option>
          <option value="family">Family</option>
          <option value="equipment">Equipment / van</option>
          <option value="other">Other</option>
        </select>
      </label>
      {error && (
        <div className="mt-2 rounded-md border border-stage-lost/40 bg-stage-lost/10 p-1 text-[12px] text-stage-lost">{error}</div>
      )}
      <div className="mt-2 flex justify-end">
        <button
          onClick={() => void handleSave()}
          disabled={saving || !startsOn || !endsOn}
          className="rounded-md bg-aubergine px-3 py-1 text-[12px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function UnavailabilityRow({ u }: { u: TechUnavailability }): JSX.Element {
  const qc = useQueryClient();
  const [removing, setRemoving] = useState(false);
  async function handleDelete(): Promise<void> {
    setRemoving(true);
    try {
      await deleteUnavailability(u.id);
      await qc.invalidateQueries({ queryKey: ["coverage"] });
    } finally {
      setRemoving(false);
    }
  }
  const fmt = (s: string): string => formatDate(s);
  const sameDay = u.startsOn === u.endsOn;
  return (
    <li className="flex items-baseline justify-between rounded-md bg-ink-800 px-2 py-1 text-[13px]">
      <span>
        <span className="font-semibold text-ink-50">
          {sameDay ? fmt(u.startsOn) : `${fmt(u.startsOn)} → ${fmt(u.endsOn)}`}
        </span>
        {u.reason && <span className="ml-2 text-ink-100">{u.reason}</span>}
      </span>
      <button
        onClick={() => void handleDelete()}
        disabled={removing}
        className="text-[11px] text-ink-100 hover:text-stage-lost"
      >
        ✕
      </button>
    </li>
  );
}

function Stat({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-ink-700 bg-ink-800 p-3">
      <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${accent ?? "text-ink-50"}`}>
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[12px] text-ink-100">{hint}</div>}
    </div>
  );
}
